/**
 * Data Source Configuration Hook
 *
 * Custom hook for managing data source configurations,
 * including CRUD operations and connection testing.
 */

import { useCallback, useState } from 'react';

interface DataSource {
  id: string;
  name: string;
  type: string;
  organizationId: string;
  enabled: boolean;
  configuration: any;
  syncSchedule: any;
  healthStatus: any;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastSyncAt?: string;
  lastSuccessfulSyncAt?: string;
}

export const useDataSourceConfiguration = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshDataSources = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call - in real app, would call actual API
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockDataSources: DataSource[] = [
        {
          id: 'example_ado_azure-devops',
          name: 'Example ADO',
          type: 'azure-devops',
          organizationId: 'example-org',
          enabled: false,
          configuration: {
            organizationUrl: 'https://dev.azure.com/example-org',
            personalAccessToken: '***masked***',
            defaultProject: 'MyProject',
            workItemTypes: ['User Story', 'Bug', 'Task', 'Feature'],
          },
          syncSchedule: {
            enabled: false,
            cronExpression: '0 */6 * * *',
            incrementalSyncEnabled: true,
            incrementalSyncHours: 24,
          },
          healthStatus: {
            status: 'unknown',
            lastCheckedAt: new Date().toISOString(),
            apiCallsToday: 0,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'system',
        },
      ];

      setDataSources(mockDataSources);
    } catch (err: any) {
      setError(err.message || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  }, []);

  const createDataSource = useCallback(async (data: Partial<DataSource>) => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newDataSource: DataSource = {
        ...(data as DataSource),
        id: `${data.name?.toLowerCase().replace(/\s+/g, '_')}_${data.type}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'current-user',
      };

      setDataSources(prev => [...prev, newDataSource]);
    } catch (err: any) {
      setError(err.message || 'Failed to create data source');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  const updateDataSource = useCallback(
    async (id: string, data: Partial<DataSource>) => {
      setLoading(true);
      setError(null);

      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        setDataSources(prev =>
          prev.map(ds =>
            ds.id === id
              ? { ...ds, ...data, updatedAt: new Date().toISOString() }
              : ds
          )
        );
      } catch (err: any) {
        setError(err.message || 'Failed to update data source');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteDataSource = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setDataSources(prev => prev.filter(ds => ds.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete data source');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const testConnection = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update health status
      setDataSources(prev =>
        prev.map(ds =>
          ds.id === id
            ? {
                ...ds,
                healthStatus: {
                  ...ds.healthStatus,
                  status: 'healthy',
                  lastCheckedAt: new Date().toISOString(),
                  responseTime: 150,
                },
              }
            : ds
        )
      );

      return {
        success: true,
        responseTime: 150,
        message: 'Connection successful',
      };
    } catch (err: any) {
      setError(err.message || 'Connection test failed');

      // Update health status to error
      setDataSources(prev =>
        prev.map(ds =>
          ds.id === id
            ? {
                ...ds,
                healthStatus: {
                  ...ds.healthStatus,
                  status: 'error',
                  lastCheckedAt: new Date().toISOString(),
                  errorMessage: err.message,
                },
              }
            : ds
        )
      );

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const startSync = useCallback(
    async (id: string, type: 'full' | 'incremental') => {
      setLoading(true);
      setError(null);

      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // Update last sync time
        setDataSources(prev =>
          prev.map(ds =>
            ds.id === id
              ? {
                  ...ds,
                  lastSyncAt: new Date().toISOString(),
                }
              : ds
          )
        );

        return `sync_job_${Date.now()}`;
      } catch (err: any) {
        setError(err.message || 'Failed to start sync');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    dataSources,
    loading,
    error,
    refreshDataSources,
    createDataSource,
    updateDataSource,
    deleteDataSource,
    testConnection,
    startSync,
  };
};
